import { getItemSummary } from "../../utils/orderItemCounts";
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapPin, Eye, EyeOff } from 'lucide-react';

// Define types for Google Maps to avoid using 'any'
type Google = typeof google;
type Map = google.maps.Map;
type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
type Marker = google.maps.Marker;
type LatLngLiteral = google.maps.LatLngLiteral;
type InfoWindow = google.maps.InfoWindow;

interface Order {
  _id: string;
  deliveryDetails: {
    coordinates?: {
      latitude: number;
      longitude: number;
    };
    postcode?: string;
    flatNumber?: string;
    address?: string;
  };
  userId: {
    name: string;
    phoneNumber?: string;
  };
  deliveryTime?: string;
  isAdhoc?: boolean;
  peopleCount: number;
  meals?: { mealId?: any }[];
  drinks?: { drinkId?: any }[];
  drinkId?: any;
  mealId?: {
    name: string;
  };
}

interface OrderLocationsMapProps {
  orders: Order[];
  apiKey?: string;
}

const DEFAULT_API_KEY = "AIzaSyCwFYHOFKgcgkXqX2z33ryuhgPOYhCNoEY";

// Fixed palette of highly distinct, bright colors
const DISTINCT_COLORS = [
  "#EF4444", // Red 500
  "#F97316", // Orange 500
  "#F59E0B", // Amber 500
  "#84CC16", // Lime 500
  "#10B981", // Emerald 500
  "#06B6D4", // Cyan 500
  "#3B82F6", // Blue 500
  "#6366F1", // Indigo 500
  "#8B5CF6", // Violet 500
  "#EC4899", // Pink 500
  "#F43F5E", // Rose 500
  "#14B8A6", // Teal 500
  "#D946EF", // Fuchsia 500
];

// Helper to get color for a slot
const getColorForTimeSlot = (slot: string, allSlots: string[]): string => {
  // We map the slot to a specific color index based on its position in the sorted unique list.
  // This ensures that if you have 5 slots, you get the first 5 distinct colors.
  const index = allSlots.indexOf(slot);
  if (index >= 0) {
    return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
  }
  return "#9CA3AF"; // Gray 400 fallback
};

const OrderLocationsMap: React.FC<OrderLocationsMapProps> = ({ orders, apiKey }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapInstance = useRef<Map | null>(null);
  const markersRef = useRef<(AdvancedMarkerElement | Marker)[]>([]);
  const infoWindowsRef = useRef<InfoWindow[]>([]);

  const finalApiKey = apiKey || DEFAULT_API_KEY;

  // State for filtering
  const [hiddenSlots, setHiddenSlots] = useState<Set<string>>(new Set());

  // Memoize valid orders
  const validOrders = useMemo(() =>
    orders.filter(
      (order): order is Order & {
        deliveryDetails: {
          coordinates: {
            latitude: number;
            longitude: number
          }
        }
      } =>
        !!order.deliveryDetails?.coordinates?.latitude &&
        !!order.deliveryDetails?.coordinates?.longitude
    ),
    [orders]
  );

  // Extract sorted unique time slots
  const uniqueTimeSlots = useMemo(() => {
    const slots = new Set<string>();
    validOrders.forEach(o => slots.add(o.deliveryTime || "Not specified"));
    return Array.from(slots).sort();
  }, [validOrders]);

  // Derived visible orders
  const visibleOrders = useMemo(() =>
    validOrders.filter(order => !hiddenSlots.has(order.deliveryTime || "Not specified")),
    [validOrders, hiddenSlots]
  );

  // Toggle slot visibility
  const toggleSlot = (slot: string) => {
    setHiddenSlots(prev => {
      const next = new Set(prev);
      if (next.has(slot)) {
        next.delete(slot);
      } else {
        next.add(slot);
      }
      return next;
    });
  };

  // Clean up markers
  const clearMarkers = () => {
    markersRef.current.forEach(marker => {
      if ('map' in marker) {
        marker.map = null;
      }
    });
    markersRef.current = [];

    infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
    infoWindowsRef.current = [];
  };

  const renderMarkers = useCallback(() => {
    if (!mapInstance.current) return;

    clearMarkers();
    const map = mapInstance.current;

    visibleOrders.forEach(order => {
      const { latitude, longitude } = order.deliveryDetails.coordinates;
      const position: LatLngLiteral = { lat: latitude, lng: longitude };
      const timeSlot = order.deliveryTime || "Not specified";
      const color = getColorForTimeSlot(timeSlot, uniqueTimeSlots);

      // Info Window Content
      const contentString = `
        <div style="padding: 8px; max-width: 200px;">
          <h3 style="margin: 0 0 5px; font-size: 14px; font-weight: bold;">${order.userId.name}</h3>
          <p style="margin: 0; font-size: 12px; color: #555;">${order.deliveryDetails.flatNumber ? order.deliveryDetails.flatNumber + ', ' : ''}${order.deliveryDetails.address || ''}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Meal:</strong> ${order.mealId?.name || 'N/A'}</p>
          <p style="margin: 0; font-size: 12px;"><strong>Count:</strong> ${order.isAdhoc ? getItemSummary(order) : `${order.peopleCount} people`}</p>
          <p style="margin: 4px 0 0; font-size: 12px; color: ${color}; font-weight: bold;">${timeSlot}</p>
          ${order.userId.phoneNumber ? `<p style="margin-top: 5px;"><a href="tel:${order.userId.phoneNumber}" style="color: #4F46E5; font-size: 12px;">${order.userId.phoneNumber}</a></p>` : ''}
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: contentString,
        ariaLabel: `Order for ${order.userId.name}`,
      });
      infoWindowsRef.current.push(infoWindow);

      let marker: AdvancedMarkerElement | Marker;

      if (window.google.maps.marker?.AdvancedMarkerElement) {
        const pinElement = document.createElement('div');
        // SVG Pin
        pinElement.innerHTML = `
          <svg width="32" height="32" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5">
             <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
             <circle cx="12" cy="9" r="3" fill="white"/>
          </svg>
        `;

        marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          map,
          title: `Order for ${order.userId.name}`,
          content: pinElement,
        });

        marker.addListener('click', () => {
          infoWindowsRef.current.forEach(iw => iw.close());
          infoWindow.open({
            anchor: marker,
            map,
          });
        });

      } else {
        // Fallback
        marker = new google.maps.Marker({
          position,
          map,
          title: `Order for ${order.userId.name}`,
          icon: {
            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: "white",
            scale: 1.5,
            anchor: new google.maps.Point(12, 24),
          }
        });

        marker.addListener('click', () => {
          infoWindowsRef.current.forEach(iw => iw.close());
          infoWindow.open(map, marker);
        });
      }

      markersRef.current.push(marker);
    });
  }, [visibleOrders, uniqueTimeSlots]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearMarkers();
      if (mapInstance.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (legendRef.current) {
          try {
            // Removing control safely check
            const map = mapInstance.current;
            map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].clear();
          } catch (e) { console.error(e) }
        }
        mapInstance.current = null;
      }
    };
  }, []);

  // Update markers when visible orders change
  useEffect(() => {
    if (mapInstance.current) {
      renderMarkers();
    }
  }, [renderMarkers]);

  // Initialize Map
  const initializeMap = useCallback(() => {
    if (!mapRef.current) return;

    // If instance exists, just center? No, init logic.
    if (mapInstance.current) {
      // If re-initialized with new orders/size, maybe fit bounds?
      // For now, let's trust the effect dependency.
      return;
    }

    if (!validOrders.length) return;

    try {
      if (!window.google?.maps) throw new Error('Google Maps API not loaded');

      // Calculate map bounds/center
      const bounds = new google.maps.LatLngBounds();
      validOrders.forEach(o => {
        bounds.extend({
          lat: o.deliveryDetails.coordinates!.latitude,
          lng: o.deliveryDetails.coordinates!.longitude
        });
      });
      const center = bounds.getCenter();

      const mapOptions: google.maps.MapOptions = {
        center: center,
        zoom: 12, // Initial zoom, bounds usually override or user zooms
        mapId: `order-locations-map-${Date.now()}`,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      };

      const map = new google.maps.Map(mapRef.current, mapOptions);
      mapInstance.current = map;

      // Auto fit bounds if many markers
      if (validOrders.length > 1) {
        map.fitBounds(bounds);
      }

      // Add Legend
      if (legendRef.current) {
        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(legendRef.current);
      }

      renderMarkers();

    } catch (error) {
      console.error('Map init error:', error);
      setMapError(error instanceof Error ? error.message : 'Map Error');
    }
  }, [validOrders, renderMarkers]);

  // Load Script
  useEffect(() => {
    if (!isMapLoaded) return;

    if (window.google?.maps) {
      initializeMap();
      return;
    }

    // Script loading logic
    const existingScript = document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`);
    if (existingScript) {
      existingScript.addEventListener('load', initializeMap);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${finalApiKey}&libraries=marker`;
    script.async = true;
    script.defer = true;
    script.onload = initializeMap;
    script.onerror = () => setMapError('Failed to load Google Maps API');
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', initializeMap);
    };
  }, [finalApiKey, initializeMap, isMapLoaded]);

  return (
    <div className="w-full h-[600px] relative">
      {!isMapLoaded ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <button
            onClick={() => setIsMapLoaded(true)}
            className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center shadow-sm"
          >
            <MapPin className="mr-2 h-5 w-5" />
            Load Map
          </button>
        </div>
      ) : mapError ? (
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 border border-dashed border-gray-300 rounded-lg">
          <MapPin className="h-10 w-10 text-gray-300 mb-4" />
          <p className="text-gray-500">{mapError}</p>
        </div>
      ) : (
        <div ref={mapRef} className="w-full h-full rounded-lg" />
      )}

      {/* Legend Component (Injected into Map) */}
      {isMapLoaded && (
        <div
          ref={legendRef}
          className="bg-white m-3 p-3 rounded-lg shadow-lg border border-gray-100 max-w-[220px] max-h-[400px] overflow-y-auto"
          style={{ fontFamily: 'sans-serif' }} // Ensure nice font inside map
        >
          <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Time Slots</h4>
            {/* Reset Filter Button */}
            {hiddenSlots.size > 0 && (
              <button
                onClick={() => setHiddenSlots(new Set())}
                className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Show All
              </button>
            )}
          </div>

          <div className="space-y-2">
            {uniqueTimeSlots.map(slot => {
              const isHidden = hiddenSlots.has(slot);
              const color = getColorForTimeSlot(slot, uniqueTimeSlots);
              const count = validOrders.filter(o => (o.deliveryTime || "Not specified") === slot).length;
              return (
                <div
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  className={`flex items-center justify-between cursor-pointer p-1.5 rounded hover:bg-gray-50 transition-colors ${isHidden ? 'opacity-50' : 'opacity-100'}`}
                >
                  <div className="flex items-center space-x-2 overflow-hidden flex-1">
                    <span
                      className={`w-3 h-3 rounded-full flex-shrink-0 transition-transform ${isHidden ? 'scale-75 bg-gray-300' : ''}`}
                      style={{ backgroundColor: isHidden ? undefined : color }}
                    />
                    <div className="flex items-center min-w-0 flex-1">
                      <span className={`text-xs text-gray-700 truncate font-medium ${isHidden ? 'line-through text-gray-400' : ''}`} title={slot}>
                        {slot}
                      </span>
                      <span className={`text-xs text-gray-500 font-normal ml-1 flex-shrink-0 ${isHidden ? 'text-gray-400' : ''}`}>({count})</span>
                    </div>
                  </div>

                  {isHidden ? (
                    <EyeOff className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 ml-2" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLocationsMap;